import { useQueryClient } from "@tanstack/react-query";
import { Badge, Card, CardBody } from "reactstrap";
import { useEffect, useState } from "react";
import { useSetAtom } from "jotai";

import FileHandler from "../../../../components/FormBuilder/CustomizationTab/BuildTab/components/FileHandler";
import GCLogo from "../../../../assets/img/growthcloud/growthcloud_wordmark.svg";
import CustomToggle from "../../../../components/Drawers/Fields/CustomToggle";
import { newToastEvent, updateToastEvent } from "../../../../utils/ToastUtil";
import useUpdateCloud from "../../../../api/api-hooks/cloud/useUpdateCloud";
import { tenantAtom, cloudLogoAtom } from "../../../../atoms/GlobalAtoms";
import AppPageHeader from "../../../../components/Headers/AppPageHeader";
import SkeletonLoader from "../../../../elements/SkeletonLoader";
import EditSaveButton from "../../../../elements/EditSaveButton";
import { getErrorMessage } from "../../../../utils/GeneralUtils";
import useCloud from "../../../../api/api-hooks/cloud/useCloud";
import TenantAPI from "../../../../api/api/TenantAPI";
import Constants from "../../../../utils/Constants";

interface Props {
  tenantID: string;
}

const Settings = ({ tenantID }: Props) => {
  const queryClient = useQueryClient();
  const { cloud, cloudLoading } = useCloud(tenantID);
  const setTenant = useSetAtom(tenantAtom);
  const setCloudLogo = useSetAtom(cloudLogoAtom);
  const [edit, setEdit] = useState(false);
  const [cloudName, setCloudName] = useState<string | undefined>(cloud?.Name);
  const { updateCloud, updatingCloud } = useUpdateCloud();
  const [colorTheme, setColorTheme] = useState(cloud?.ColorTheme ?? "#ffffff");
  const [logo, setLogo] = useState(cloud?.Logo ?? null);
  const [chatWithFiles, setChatWithFiles] = useState(
    cloud?.FileChunkIndex === "Built" ? true : false
  );
  const isBuilding = cloud?.FileChunkIndex === "Building";

  useEffect(() => {
    if (cloudLoading) return;
    setCloudName(cloud?.Name);
    setColorTheme(cloud?.ColorTheme ?? "#ffffff");
    setLogo(cloud?.Logo ?? null);
  }, [cloudLoading, cloud]);

  return (
    <>
      <div className="border-bottom border-lighter">
        <AppPageHeader
          title="General Settings"
          size="sm"
          className="py-3"
          description="Edit cloud name, theme and logo"
          customContent={
            tenantID === Constants.SUP_CLOUD_ID ? null : (
              <EditSaveButton
                isEditing={edit}
                setIsEditing={setEdit}
                onSave={() => {
                  updateCloudSettings();
                  updateCloudFileChunkIndex();
                }}
                isSubmitting={updatingCloud}
              />
            )
          }
        />
      </div>
      <div className="tw:mt-6">
        {!edit ? (
          <>
            <h4>Cloud Name</h4>
            <Card className="border">
              <CardBody className="p-2 text">
                {cloudLoading ? (
                  <SkeletonLoader css="m-0 p-0" maxWidth={100} height={22} />
                ) : (
                  <>
                    <h4 className="p-0 m-0">{cloudName}</h4>
                  </>
                )}
              </CardBody>
            </Card>
            {/* <h4>Header color theme</h4>
            <AppColorPicker
              value={colorTheme}
              onChange={() => {}}
              disabled={true}
            /> */}

            <h4 className="tw:mt-6 tw:mb-4">Custom Logo</h4>
            <img
              src={logo ? logo?.PublicURL : GCLogo}
              alt="Logo"
              style={{ width: 300, marginLeft: 10 }}
            />
            <div className="tw:flex tw:justify-between tw:items-center tw:mb-3 tw:mt-8">
              <div>
                <h4 className="tw:mb-1">Chat With Files</h4>
                <div className="small">
                  If enabled, Brie will be able to chat with files.
                </div>
              </div>
              <Badge
                color={
                  isBuilding ? "info" : chatWithFiles ? "primary" : "danger"
                }
              >
                {isBuilding
                  ? "Building"
                  : chatWithFiles
                  ? "Enabled"
                  : "Not Enabled"}
              </Badge>
            </div>
          </>
        ) : (
          <>
            <h4>Cloud Name</h4>
            <input
              type="text"
              className="form-control"
              onChange={(e) => setCloudName(e.target.value)}
              value={cloudName}
            />
            {/* <h4 className="mt-4">Header color theme</h4>
            <AppColorPicker
              value={colorTheme}
              onChange={(v) => {
                setColorTheme(v);
              }}
            /> */}
            <h4 className="tw:mt-6 tw:mb-4">Custom Logo</h4>
            <div className="w-25">
              <FileHandler
                recordID={tenantID}
                moduleAPIName="Tenant"
                file={logo}
                setFile={setLogo}
                publicContainer={true}
                handleFileData={(v) => {
                  if (v?.files?.length) setLogo(v?.files[0]);
                }}
                style={{ width: 300, marginLeft: 10 }}
                sources={[
                  {
                    Source: "Record",
                    SourceID: tenantID,
                    SourceModule: "Tenant",
                    FieldAPIName: "Logo",
                  },
                ]}
                fieldAPIName="Logo"
              />
            </div>
            <div className="tw:flex tw:justify-between tw:items-center tw:mb-3 tw:mt-8">
              <div>
                <h4 className="tw:mb-1">Chat With Files</h4>
                <div className="small">
                  If enabled, Brie will be able to chat with files.
                </div>
              </div>
              {isBuilding ? (
                <Badge color="info">Building</Badge>
              ) : (
                <CustomToggle
                  value={chatWithFiles}
                  containerCSS="tw:pt-4"
                  disabled={cloud?.FileChunkIndex === "Built"}
                  handleChange={(e) => {
                    const checked = e.target.checked;

                    setChatWithFiles(checked);
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );

  function updateCloudSettings() {
    if (
      (cloud?.Name === cloudName &&
        cloud?.Logo?.PublicURL === logo?.PublicURL &&
        cloud?.ColorTheme === colorTheme) ||
      !cloudName
    )
      return setEdit(false);

    updateCloud({
      tenantID,
      payload: {
        Name: cloudName,
        Logo: logo,
        ColorTheme: colorTheme,
      },
      onSuccess: () => {
        setTenant((prev) => (prev ? { ...prev, TenantName: cloudName } : prev));
        queryClient.setQueryData<any>(["user", tenantID], (prev: any) =>
          prev
            ? {
                ...prev,
                user: {
                  ...prev.user,
                  Tenants:
                    prev?.user?.Tenants?.map((t: any) =>
                      t.TenantID === tenantID
                        ? {
                            ...t,
                            TenantName: cloudName,
                            Logo: logo,
                            ColorTheme: colorTheme,
                          }
                        : t
                    ) || [],
                },
              }
            : prev
        );

        setCloudLogo(logo?.PublicURL ?? "");
        setEdit(false);
      },
    });
  }

  async function updateCloudFileChunkIndex() {
    if (chatWithFiles && cloud?.FileChunkIndex === "Built") return;
    if (!chatWithFiles) return;

    newToastEvent(
      "success",
      "Adding File Index to Cloud...",
      "file-chunk-index",
      {
        autoClose: false,
        loading: true,
      }
    );

    try {
      await TenantAPI.updateCloudFileChunkIndex(tenantID);

      queryClient.invalidateQueries({ queryKey: ["cloud", tenantID] });

      updateToastEvent(
        "success",
        "File Index is now being built! This may take a few minutes.",
        "file-chunk-index",
        {
          autoClose: true,
          loading: false,
        }
      );
    } catch (err) {
      updateToastEvent("error", "file-chunk-index", getErrorMessage(err), {
        autoClose: true,
        loading: false,
      });
    }
  }
};

export default Settings;
